import React, { useState, useEffect } from "react";
import {
  getTenantServices,
  createTenantService,
  deleteTenantServiceById,
} from "../../../shared/services/apis/tenant";
import { getBrandServicesByBrand } from "../../../shared/services/apis/brandServices";
import { useBrand } from "../../../context/brandContext";
import type {
  TenantServiceWithOptions,
  TenantServiceOptionData,
} from "../../../shared/types/tenant";
import type { BrandService } from "../../../shared/types/services";
import TenantServiceOptionsModal from "./TenantServiceOptionsModal";
import {
  AiOutlinePlus,
  AiOutlineDelete,
  AiOutlineEdit,
} from "react-icons/ai";
import { 
  HiOutlineInformationCircle,
  HiOutlineSparkles,
} from "react-icons/hi";
import { 
  FiSettings,
  FiLayers,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import Dialog from "../../../shared/ui/dialog";
import { PREDEFINED_SERVICE_CONFIGS, PREDEFINED_SERVICE_NAMES } from "../../../constants/tenantService";

interface TenantServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
  onServiceUpdate?: () => void;
}

interface NewServiceData {
  name: string;
  description: string;
  brandServiceId: string;
  options: TenantServiceOptionData[];
}

const TenantServiceModal: React.FC<TenantServiceModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  onServiceUpdate,
}) => {
  const { brand } = useBrand();
  const [services, setServices] = useState<TenantServiceWithOptions[]>([]);
  const [brandServices, setBrandServices] = useState<BrandService[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingBrandServices, setLoadingBrandServices] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [optionsModalData, setOptionsModalData] = useState<{
    serviceId: string;
    serviceName: string;
  } | null>(null);
  const [newService, setNewService] = useState<NewServiceData>({
    name: "",
    description: "",
    brandServiceId: "",
    options: [],
  });


  useEffect(() => {
    if (isOpen && tenantId) {
      fetchServices();
    }
  }, [isOpen, tenantId]);

  useEffect(() => {
    if (isOpen && brand?.id) {
      fetchBrandServices();
    }
  }, [isOpen, brand?.id]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedServices = await getTenantServices(tenantId);
      setServices(fetchedServices);
    } catch (err) {
      setError("Failed to fetch services");
      console.error("Error fetching services:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandServices = async () => {
    if (!brand?.id) return;

    try {
      setLoadingBrandServices(true);
      const fetchedBrandServices = await getBrandServicesByBrand(brand.id);
      // Filter only active brand services
      const activeBrandServices = fetchedBrandServices.filter(
        (bs) => bs.isActive
      );
      setBrandServices(activeBrandServices);
    } catch (err) {
      console.error("Error fetching brand services:", err);
      setError("Failed to fetch available services");
    } finally {
      setLoadingBrandServices(false);
    }
  };

  // Get available service names (exclude already assigned ones)
  const getAvailableServiceNames = () => {
    const existingServiceNames = services.map((service) => service.name);
    return PREDEFINED_SERVICE_NAMES.filter(
      (name) => !existingServiceNames.includes(name)
    );
  };

  // Enhanced service creation to include predefined options
  const handleCreateService = async () => {
    if (!newService.name.trim()) {
      setError("Service name is required");
      return;
    }

    if (!newService.brandServiceId) {
      setError("Please select a service type");
      return;
    }
    // Check if we've reached the maximum limit of 3 services
    if (services.length > 9) {
      setError("Maximum of 8 tenant services allowed");
      return;
    }

    // Check if the service name already exists
    const existingService = services.find(
      (service) => service.name === newService.name
    );
    if (existingService) {
      setError("A service with this name already exists");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get the service options based on the selected service name
      const serviceOptions =
        PREDEFINED_SERVICE_CONFIGS[newService.name as keyof typeof PREDEFINED_SERVICE_CONFIGS] || [];

      await createTenantService({
        tenantId,
        brandServiceId: newService.brandServiceId,
        name: newService.name,
        description: newService.description,
        options: serviceOptions,
      });

      await fetchServices();
      setShowAddForm(false);
      setNewService({
        name: "",
        description: "",
        brandServiceId: "",
        options: [],
      });

      if (onServiceUpdate) {
        onServiceUpdate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create service");
      console.error("Error creating service:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!confirm("Are you sure you want to delete this service?")) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await deleteTenantServiceById(serviceId);
      await fetchServices();

      if (onServiceUpdate) {
        onServiceUpdate();
      }
    } catch (err) {
      setError("Failed to delete service");
      console.error("Error deleting service:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageOptions = (serviceId: string, serviceName: string) => {
    setOptionsModalData({ serviceId, serviceName });
  };

  const handleOptionsUpdate = () => {
    fetchServices();
    if (onServiceUpdate) {
      onServiceUpdate();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        title={`Manage Tenant Services - ${tenantName}`}
      >
       <div>
          {/* Enhanced Header */}
          {/* <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FiLayers className="text-gray-600" />
                  Tenant Services
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Configure services for <span className="font-medium text-gray-700">{tenantName}</span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-white px-3 py-1 rounded-full border border-gray-200">
                  <span className="text-xs font-medium text-gray-600">
                    {services.length}/3 Services
                  </span>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <AiOutlineClose size={20} />
                </button>
              </div>
            </div>
          </div> */}

          {/* Content */}
          <div>
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-start gap-3">
                <HiOutlineInformationCircle className="text-red-500 mt-0.5 flex-shrink-0" size={20} />
                <div>
                  <p className="font-medium">Error</p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Enhanced Add Service Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                {/* <h3 className="text-lg font-semibold text-gray-900">Available Services</h3> */}
                {getAvailableServiceNames().length > 0 && (
                  <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.02]"
                  >
                    <AiOutlinePlus size={16} />
                    Add Service
                    <HiOutlineSparkles size={14} className="opacity-75" />
                  </button>
                )}
              </div>
{/* 
              {getAvailableServiceNames().length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                  <HiOutlineInformationCircle className="text-amber-600 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-medium text-amber-800">All services configured</p>
                    <p className="text-sm text-amber-700">You have reached the maximum of 3 tenant services.</p>
                  </div>
                </div>
              )} */}
            </div>

            {/* Enhanced Add Service Form */}
            {showAddForm && (
              <div className="mb-8 rounded-2xl border border-gray-200 shadow-sm" style={{backgroundColor: '#FDFDFD'}}>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <HiOutlineSparkles className="text-gray-600" size={16} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Create New Service</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">
                          Service Type *
                        </label>
                        {loadingBrandServices ? (
                          <div className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-600 border-t-transparent"></div>
                            <span className="text-sm text-gray-600">Loading available services...</span>
                          </div>
                        ) : (
                          <select
                            value={newService.brandServiceId}
                            onChange={(e) =>
                              setNewService({
                                ...newService,
                                brandServiceId: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all"
                          >
                            <option value="">Choose service type</option>
                            {brandServices.map((brandService) => (
                              <option key={brandService.id} value={brandService.id}>
                                {brandService.service?.name || "Unknown Service"}
                                {brandService.service?.description &&
                                  ` - ${brandService.service.description}`}
                              </option>
                            ))}
                          </select>
                        )}
                        {brandServices.length === 0 && !loadingBrandServices && (
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <p className="text-sm text-orange-700">
                              No services available for this brand. Contact your administrator.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">
                          Service Name *
                        </label>
                        <select
                          value={newService.name}
                          onChange={(e) =>
                            setNewService({ ...newService, name: e.target.value })
                          }
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all"
                        >
                          <option value="">Choose service name</option>
                          {getAvailableServiceNames().map((serviceName) => (
                            <option key={serviceName} value={serviceName}>
                              {serviceName}
                            </option>
                          ))}
                        </select>
                        {getAvailableServiceNames().length > 0 && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <FiCheckCircle size={12} />
                            {getAvailableServiceNames().length} service(s) available
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={newService.description}
                        onChange={(e) =>
                          setNewService({
                            ...newService,
                            description: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all resize-none"
                        rows={3}
                        placeholder="Describe what this service will provide..."
                      />
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateService}
                        disabled={
                          loading ||
                          !newService.brandServiceId ||
                          !newService.name.trim()
                        }
                        className="px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] shadow-sm hover:shadow-md"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            Creating...
                          </span>
                        ) : (
                          "Create Service"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Services List */}
            {loading && !showAddForm ? (
              <div className="flex flex-col justify-center items-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-gray-600 border-t-transparent mb-4"></div>
                <span className="text-gray-600 font-medium">Loading services...</span>
                <span className="text-sm text-gray-500">Please wait while we fetch your services</span>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Configured Services</h3>
                  {services.length > 0 && (
                    <span className="text-sm text-gray-500">
                      {services.length} of 6 services configured
                    </span>
                  )}
                </div>

                {services.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FiLayers className="text-gray-400" size={24} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No services configured</h3>
                    <p className="text-gray-500 mb-6">Get started by creating your first tenant service</p>
                    {getAvailableServiceNames().length > 0 && (
                      <button
                        onClick={() => setShowAddForm(true)}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all shadow-sm hover:shadow-md transform hover:scale-[1.02]"
                      >
                        <AiOutlinePlus size={16} />
                        Create First Service
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid gap-6">
                    {services.map((service, index) => (
                      <div
                        key={service.id}
                        className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                      >
                        {/* Service Header */}
                        <div className="p-6 border-b border-gray-100">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="text-xl  text-gray-700">
                                    {service.name}
                                  </h4>
                                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    service.isActive 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {service.isActive ? 'Active' : 'Inactive'}
                                  </div>
                                </div>
                                {service.description && (
                                  <p className="text-gray-600 mb-3 leading-relaxed">
                                    {service.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-6 text-sm text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <FiSettings size={14} />
                                    <span>{service.options?.length || 0} Options</span>
                                  </div>
                                  {service.brandService?.service && (
                                    <div className="flex items-center gap-1">
                                      <FiLayers size={14} />
                                      <span>Type: {service.brandService.service.name}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleManageOptions(service.id, service.name)}
                                className="inline-flex cursor-pointer items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors text-sm font-medium"
                                title="Manage service options"
                              >
                                <FiSettings size={16} />
                                Configure
                              </button>
                              <button
                                onClick={() => handleDeleteService(service.id)}
                                className="p-2 text-gray-500 hover:text-gray-700 cursor-pointer hover:bg-gray-50 rounded-xl transition-colors"
                                title="Delete service"
                              >
                                <AiOutlineDelete size={18} />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Service Options Preview */}
                        {service.options && service.options.length > 0 && (
                          <div className="p-6" style={{backgroundColor: '#FDFDFD'}}>
                            <div className="flex items-center justify-between mb-4">
                              <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                <FiSettings size={14} />
                                Service Configuration
                              </h5>
                              <button
                                onClick={() => handleManageOptions(service.id, service.name)}
                                className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1 font-medium"
                              >
                                <AiOutlineEdit size={12} />
                                Edit All
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {service.options.slice(0, 6).map((option) => (
                                <div
                                  key={option.id}
                                  className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="font-medium text-gray-800 text-sm capitalize">
                                      {option.key.replace(/_/g, ' ')}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {option.isActive ? (
                                        <FiCheckCircle className="text-green-500" size={12} />
                                      ) : (
                                        <FiXCircle className="text-gray-400" size={12} />
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="text-xs text-gray-600 mb-2 truncate" title={option.value || "No value"}>
                                    {option.value || "No value"}
                                  </div>
                                  
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                      {option.valueType}
                                    </span>
                                    {option.isRequired && (
                                      <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded font-medium">
                                        Required
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                              
                              {service.options.length > 6 && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-center">
                                  <div className="text-center">
                                    <div className="text-sm font-medium text-gray-700">
                                      +{service.options.length - 6} more
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      options available
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Dialog>

      {/* Options Management Modal */}
      {optionsModalData && (
        <TenantServiceOptionsModal
          isOpen={true}
          onClose={() => setOptionsModalData(null)}
          tenantId={tenantId}
          serviceId={optionsModalData.serviceId}
          serviceName={optionsModalData.serviceName}
          onOptionsUpdate={handleOptionsUpdate}
        />
      )}
    </>
  );
};

export default TenantServiceModal;
